import { useCallback, useEffect, useRef, useState } from 'react'

import { sfx } from '../../../lib/sound'
import { TouchPad, type GameProps } from '../GameShell'

const COLS = 10
const ROWS = 18
const START_MS = 700
const MIN_MS = 120

// Each piece is a list of rotation states, so rotation is a lookup rather than
// a matrix transform with kick tables to get wrong.
const PIECES: { cells: [number, number][][]; color: string }[] = [
  // I
  {
    color: '#22d3ee',
    cells: [
      [[0, 0], [0, 1], [0, 2], [0, 3]],
      [[0, 0], [1, 0], [2, 0], [3, 0]],
    ],
  },
  // O
  { color: '#facc15', cells: [[[0, 0], [0, 1], [1, 0], [1, 1]]] },
  // T
  {
    color: '#c084fc',
    cells: [
      [[0, 1], [1, 0], [1, 1], [1, 2]],
      [[0, 1], [1, 1], [1, 2], [2, 1]],
      [[1, 0], [1, 1], [1, 2], [2, 1]],
      [[0, 1], [1, 0], [1, 1], [2, 1]],
    ],
  },
  // S
  {
    color: '#4ade80',
    cells: [
      [[0, 1], [0, 2], [1, 0], [1, 1]],
      [[0, 0], [1, 0], [1, 1], [2, 1]],
    ],
  },
  // Z
  {
    color: '#f87171',
    cells: [
      [[0, 0], [0, 1], [1, 1], [1, 2]],
      [[0, 1], [1, 0], [1, 1], [2, 0]],
    ],
  },
  // J
  {
    color: '#60a5fa',
    cells: [
      [[0, 0], [1, 0], [1, 1], [1, 2]],
      [[0, 1], [0, 2], [1, 1], [2, 1]],
      [[1, 0], [1, 1], [1, 2], [2, 2]],
      [[0, 1], [1, 1], [2, 0], [2, 1]],
    ],
  },
  // L
  {
    color: '#fb923c',
    cells: [
      [[0, 2], [1, 0], [1, 1], [1, 2]],
      [[0, 1], [1, 1], [2, 1], [2, 2]],
      [[1, 0], [1, 1], [1, 2], [2, 0]],
      [[0, 0], [0, 1], [1, 1], [2, 1]],
    ],
  },
]

type Board = (string | null)[][]

function emptyBoard(): Board {
  return Array.from({ length: ROWS }, () => Array<string | null>(COLS).fill(null))
}

interface Falling {
  piece: number
  rotation: number
  row: number
  col: number
}

function cellsOf(f: Falling): [number, number][] {
  const shape = PIECES[f.piece].cells
  return shape[f.rotation % shape.length].map(
    ([r, c]) => [f.row + r, f.col + c] as [number, number],
  )
}

function fits(board: Board, f: Falling): boolean {
  return cellsOf(f).every(
    ([r, c]) =>
      c >= 0 && c < COLS && r < ROWS && (r < 0 || board[r][c] === null),
  )
}

function spawnPiece(): Falling {
  return {
    piece: Math.floor(Math.random() * PIECES.length),
    rotation: 0,
    row: 0,
    col: Math.floor(COLS / 2) - 1,
  }
}

export default function Blocks({ onGameOver, onScore }: GameProps) {
  const [board, setBoard] = useState<Board>(emptyBoard)
  const [falling, setFalling] = useState<Falling>(spawnPiece)
  const [score, setScore] = useState(0)
  const [lines, setLines] = useState(0)
  const [over, setOver] = useState(false)

  const scoreRef = useRef(0)
  const overRef = useRef(false)

  const drop = useCallback(() => {
    if (overRef.current) return

    setFalling((current) => {
      const moved = { ...current, row: current.row + 1 }
      if (fits(board, moved)) return moved

      // Landed: bake it into the board and clear any full rows.
      const next = board.map((row) => [...row])
      let aboveTop = false
      for (const [r, c] of cellsOf(current)) {
        if (r < 0) aboveTop = true
        else next[r][c] = PIECES[current.piece].color
      }

      if (aboveTop) {
        overRef.current = true
        setOver(true)
        onGameOver(scoreRef.current)
        return current
      }

      const kept = next.filter((row) => row.some((cell) => cell === null))
      const cleared = ROWS - kept.length
      if (cleared > 0) {
        // Clearing several rows at once is worth disproportionately more --
        // that is the whole reason to stack rather than survive.
        const gained = [0, 100, 300, 500, 800][cleared] ?? 800
        scoreRef.current += gained
        setScore(scoreRef.current)
        onScore?.(scoreRef.current)
        setLines((l) => l + cleared)
        sfx.complete()
        while (kept.length < ROWS) kept.unshift(Array<string | null>(COLS).fill(null))
      } else {
        sfx.tick()
      }

      setBoard(kept)

      const fresh = spawnPiece()
      if (!fits(kept, fresh)) {
        overRef.current = true
        setOver(true)
        onGameOver(scoreRef.current)
      }
      return fresh
    })
  }, [board, onGameOver, onScore])

  // Gravity, quickening as lines are cleared.
  useEffect(() => {
    if (over) return
    const interval = Math.max(MIN_MS, START_MS - lines * 25)
    const id = setInterval(drop, interval)
    return () => clearInterval(id)
  }, [drop, lines, over])

  const nudge = useCallback(
    (direction: 'left' | 'right' | 'down' | 'up') => {
      if (overRef.current) return
      setFalling((current) => {
        if (direction === 'down') {
          const moved = { ...current, row: current.row + 1 }
          return fits(board, moved) ? moved : current
        }
        if (direction === 'up') {
          const shape = PIECES[current.piece].cells
          const rotated = {
            ...current,
            rotation: (current.rotation + 1) % shape.length,
          }
          // Try nudging sideways if a rotation would clip a wall, which is what
          // stops pieces jamming against the edges.
          for (const shift of [0, -1, 1, -2, 2]) {
            const candidate = { ...rotated, col: rotated.col + shift }
            if (fits(board, candidate)) return candidate
          }
          return current
        }
        const moved = {
          ...current,
          col: current.col + (direction === 'left' ? -1 : 1),
        }
        return fits(board, moved) ? moved : current
      })
    },
    [board],
  )

  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      const map: Record<string, 'left' | 'right' | 'down' | 'up'> = {
        ArrowLeft: 'left',
        ArrowRight: 'right',
        ArrowDown: 'down',
        ArrowUp: 'up',
        a: 'left',
        d: 'right',
        s: 'down',
        w: 'up',
      }
      if (event.key === ' ') {
        // Hard drop.
        event.preventDefault()
        for (let i = 0; i < ROWS; i++) nudge('down')
        return
      }
      const direction = map[event.key]
      if (direction) {
        event.preventDefault()
        nudge(direction)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [nudge])

  const active = new Map(
    cellsOf(falling)
      .filter(([r]) => r >= 0)
      .map(([r, c]) => [`${r},${c}`, PIECES[falling.piece].color]),
  )

  return (
    <div>
      <p className="mb-2 text-center text-sm text-muted">
        {score} points · {lines} lines · ↑ rotates, space drops
      </p>
      <div
        className="mx-auto grid w-full max-w-[280px] gap-px rounded-xl bg-border-subtle p-px"
        style={{ gridTemplateColumns: `repeat(${COLS}, minmax(0, 1fr))` }}
      >
        {board.flatMap((row, r) =>
          row.map((cell, c) => {
            const color = active.get(`${r},${c}`) ?? cell
            return (
              <div
                key={`${r}-${c}`}
                className="aspect-square rounded-[2px]"
                style={{ background: color ?? 'var(--color-surface)' }}
              />
            )
          }),
        )}
      </div>
      <TouchPad onDirection={nudge} />
    </div>
  )
}
